import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { useCart } from '../../contexts/CartContext';
import { showToast } from '../Toast';
import { getProductById } from '../../data/products';
import { analyzeAndSanitize } from '../../utils/sanitizeHtml';
import { hasDownloadOption, getPhysicalLabel, getDownloadLabel, buildCartItem } from '../../utils/productVariants';
import './ProductDetailPage.css';

function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // Track selected image
  const { currentUser, isMember, getMemberPrice } = useAuth();
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        // First try to get from Firebase
        const productDoc = await getDoc(doc(db, 'products', id));
        if (productDoc.exists()) {
          setProduct({ id: productDoc.id, ...productDoc.data() });
        } else {
          // Fall back to static data
          const staticProduct = getProductById(id);
          if (staticProduct) {
            setProduct(staticProduct);
          } else {
            setError('Product not found');
          }
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        // Try static data as fallback
        const staticProduct = getProductById(id);
        if (staticProduct) {
          setProduct(staticProduct);
        } else {
          setError('Failed to load product');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  const handleAddToCart = (variant = 'physical') => {
    try {
      const cartItem = buildCartItem(product, variant);
      addToCart(cartItem);
      showToast(`${cartItem.name} added to basket!`, 'success');
    } catch (error) {
      console.error('Error adding to cart:', error);
      showToast('Failed to add item to basket. Please try again.', 'error');
    }
  };

  const formatPrice = (price) => {
    return `${price} SEK`;
  };

  // Ensure price is a valid number
  const originalPrice = product ? product.price || 0 : 0;
  const displayPrice = currentUser && isMember() && originalPrice > 0 ? getMemberPrice(originalPrice) : originalPrice;
  const showMemberDiscount = currentUser && isMember() && displayPrice < originalPrice;

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'tickets': return 'EVENT TICKET';
      case 'merchandise': return 'MERCH';
      case 'crafts': return 'HANDMADE';
      case 'recordings': return 'DIGITAL';
      default: return category ? category.toUpperCase() : '';
    }
  };

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="loading">Loading product...</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail-page">
        <div className="error">
          <h2>Product Not Found</h2>
          <p>{error || 'The product you are looking for does not exist.'}</p>
          <button onClick={() => navigate(-1)} className="btn btn--outline">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Get all product images - use thumbnail as the main display image
  const getProductImages = () => {
    // If there's a thumbnail, use it as the first image
    if (product && product.thumbnailUrl) {
      // If there are other images, add them after the thumbnail
      if (product.images && product.images.length > 0) {
        // Don't duplicate if thumbnail is already in images
        const otherImages = product.images.filter(img => img !== product.thumbnailUrl);
        return [product.thumbnailUrl, ...otherImages];
      }
      return [product.thumbnailUrl];
    }
    // Fallback to regular images if no thumbnail
    else if (product && product.images && product.images.length > 0) {
      return product.images;
    } else if (product && (product.imageUrl || product.image)) {
      return [product.imageUrl || product.image];
    }
    return [];
  };

  const productImages = getProductImages();

  return (
    <div className="product-detail-page">
      {/* Halftone band: product image + thumbs + right-side info */}
      <div className="halftone-band">
        <div className="product-detail-columns">
          {/* Left Column - Image & Thumbnails */}
          <div className="product-detail-column">
            {productImages.length > 0 && (
              <div className="product-main-image">
                <img 
                  src={productImages[selectedImageIndex]} 
                  alt={`${product.name} - Image ${selectedImageIndex + 1}`} 
                />
              </div>
            )}

            {productImages.length > 1 && (
              <div className="product-thumbnail-gallery">
                {productImages.map((image, index) => (
                  <div 
                    key={index}
                    className={`thumbnail ${index === selectedImageIndex ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img 
                      src={image} 
                      alt={`${product.name} thumbnail ${index + 1}`} 
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Product Info */}
          <div className="product-detail-column">
            <div className="product-info-panel">
              {/* Category badge removed from view */}
              
              <h1 className="product-title">{product.name}</h1>

              {!hasDownloadOption(product) && (
                <div className="product-pricing">
                  <span className="product-price">{formatPrice(originalPrice)}</span>
                </div>
              )}

              {/* Stock and availability info hidden but kept for future use */}
              <div className="product-details" style={{ display: 'none' }}>
                {product.type === 'physical' && (
                  <div className="product-stock">
                    Stock: <strong>{product.stock || 0} LEFT</strong>
                  </div>
                )}
                
                {product.type === 'digital' && (
                  <div className="product-availability">
                    <strong>INSTANT DOWNLOAD</strong>
                  </div>
                )}
              </div>

              {hasDownloadOption(product) ? (
                <div className="product-variant-options">
                  <div className="product-variant-row">
                    <span className="product-variant-label">{getPhysicalLabel(product)}</span>
                    <button
                      className="add-to-cart-btn-detail"
                      onClick={() => handleAddToCart('physical')}
                      disabled={!product.inStock}
                    >
                      {product.inStock ? `Add to basket: ${formatPrice(originalPrice)}` : 'OUT OF STOCK'}
                    </button>
                  </div>
                  <div className="product-variant-row">
                    <span className="product-variant-label">{getDownloadLabel(product)}</span>
                    <button
                      className="add-to-cart-btn-detail"
                      onClick={() => handleAddToCart('download')}
                    >
                      {`Add to basket: ${formatPrice(product.downloadPrice)}`}
                    </button>
                  </div>
                </div>
              ) : (
                <button 
                  className="add-to-cart-btn-detail"
                  onClick={() => handleAddToCart('physical')}
                  disabled={!product.inStock}
                >
                  {product.inStock ? `Add to basket: ${formatPrice(originalPrice)}` : 'OUT OF STOCK'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Description section with grey/green background */}
      <div className="product-description-section">
        <div className="product-description-container">
          <div className="product-description-text">
            <h2>Product Details</h2>
            {product.richDescription ? (
              <div
                className="rte-display"
                dangerouslySetInnerHTML={{ 
                  __html: (() => {
                    // Re-sanitize the content to ensure iframes are properly handled
                    // This is safe because the content was already sanitized when saved
                    const { html } = analyzeAndSanitize(product.richDescription);
                    return html;
                  })()
                }}
              />
            ) : (
              <p>{product.description || 'No description available for this product.'}</p>
            )}
            
            {/* Additional product information */}
            {product.features && (
              <div className="product-features">
                <h3>Features</h3>
                <ul>
                  {product.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {product.specifications && (
              <div className="product-specifications">
                <h3>Specifications</h3>
                <ul>
                  {Object.entries(product.specifications).map(([key, value]) => (
                    <li key={key}>
                      <strong>{key}:</strong> {value}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetailPage;
